/**
 * THE BUTTON SKIN — the layered sheet behind `engine/ui/button.ts`.
 *
 * Its own import-free module so `styles.ts` (which every ui primitive calls)
 * can inject it without an import cycle through `button.ts`. The design notes
 * — variants, depth, the transform contract, why @layer components — are in
 * the header of `button.ts`. No backticks inside: one template literal.
 */
export const BUTTON_STYLE_MARK = 'ui-button-css';

export const BUTTON_CSS = `
@layer components{
/* THE ONE BUTTON FAMILY (owner rulings 2026-10-14, dusk palette): every
   variant is a muted fill with a thick darker LIP under it, a soft gloss band
   and a shadow tinted with the lip colour. Gold primary takes navy ink; teal
   secondary, coral danger, indigo icon and the navy pill take cream ink with a
   dark stroke. Colours mirror the painted skins in public/ui/skins/. */
.rga-btn{--rga-btn-face:#3A8582;--rga-btn-face-2:#22605F;--rga-btn-rim:#123C40;
  --rga-btn-lip:#0C2C30;--rga-btn-ink:var(--color-on-fill,#F6EBD3);--rga-btn-depth:5px;
  --rga-btn-stroke:0 1px 0 rgba(4,9,24,.6),0 0 2px rgba(4,9,24,.45);text-shadow:var(--rga-btn-stroke);font-weight:700;
  position:relative;isolation:isolate;gap:.5rem;padding:.55rem 1.1rem;
  border-radius:var(--rga-radius-control,12px);border:var(--rga-rim,2px) solid var(--rga-btn-rim);
  background:linear-gradient(180deg,var(--rga-btn-face),var(--rga-btn-face-2));
  color:var(--rga-btn-ink);font-family:var(--font-heading,inherit);font-size:.95rem;
  line-height:1.1;letter-spacing:.02em;white-space:nowrap;cursor:pointer;user-select:none;
  -webkit-tap-highlight-color:transparent;
  box-shadow:0 var(--rga-btn-depth) 0 var(--rga-btn-lip),
    0 calc(var(--rga-btn-depth) + 4px) 12px color-mix(in srgb,var(--rga-btn-lip) 45%,transparent),
    inset 0 2px 0 rgba(255,255,255,.30),inset 0 -3px 0 rgba(0,0,0,.18);
  transition:transform 140ms cubic-bezier(.34,1.56,.64,1),box-shadow 120ms ease,
    filter 120ms ease,scale 140ms cubic-bezier(.34,1.56,.64,1),
    translate 140ms cubic-bezier(.34,1.56,.64,1),opacity 140ms linear}
/* The gloss band: the one hard highlight the painted icon set carries too. */
.rga-btn::before{content:'';position:absolute;z-index:-1;inset:2px 6px auto 6px;height:42%;
  border-radius:9px 9px 14px 14px;pointer-events:none;
  background:linear-gradient(180deg,rgba(255,255,255,.26),rgba(255,255,255,.03))}
.rga-btn__label{display:inline-block;transition:opacity 90ms linear}
.rga-btn__icon{filter:drop-shadow(0 1px 0 rgba(4,9,24,.6))}
.rga-btn:hover{filter:brightness(1.08) saturate(1.05)}
/* Held: sink onto the lip and squash. Both ride on the INDIVIDUAL translate
   and scale properties, never on transform or on --rga-press-y: the unlayered
   .rga-press base rule resets that variable and would beat this layered sheet.
   translate and scale compose with press.ts's transform instead of fighting it. */
.rga-btn.is-pressed,.rga-btn:active{translate:0 var(--rga-btn-depth);scale:1.02 .95;transition-duration:90ms;
  box-shadow:0 0 0 var(--rga-btn-lip),0 2px 5px color-mix(in srgb,var(--rga-btn-lip) 40%,transparent),
    inset 0 1px 0 rgba(255,255,255,.18),inset 0 3px 7px rgba(0,0,0,.3)}
.rga-btn.is-released{animation:rga-btn-release 260ms cubic-bezier(.34,1.56,.64,1)}
@keyframes rga-btn-release{0%{scale:1.02 .95}45%{scale:.98 1.04}75%{scale:1.008 .992}100%{scale:1 1}}
.rga-btn[disabled],.rga-btn[aria-disabled=true]{filter:saturate(.35);scale:1 1;translate:0 0;
  box-shadow:0 2px 0 var(--rga-btn-lip),inset 0 1px 0 rgba(255,255,255,.2)}
.rga-btn:focus-visible{outline:3px solid var(--color-gold,#F2C14E);outline-offset:3px}

/* ── variants ─────────────────────────────────────────────────────────── */
.rga-btn--primary{--rga-btn-face:#F5D27E;--rga-btn-face-2:#D9A13F;--rga-btn-rim:#7A4E10;
  --rga-btn-lip:#5E3B08;--rga-btn-ink:#14213D;font-weight:800;--rga-btn-depth:6px;
  --rga-btn-stroke:0 1px 0 rgba(255,240,200,.45)}
.rga-btn--primary:hover{filter:brightness(1.06) saturate(1.1)}
.rga-btn--secondary{}
.rga-btn--ghost{--rga-btn-face:transparent;--rga-btn-face-2:transparent;--rga-btn-rim:transparent;
  --rga-btn-lip:transparent;--rga-btn-ink:var(--color-text-dim,#BCC6D9);--rga-btn-depth:0px;box-shadow:none;text-shadow:none}
.rga-btn--ghost::before{display:none}
.rga-btn--ghost:hover{--rga-btn-ink:var(--color-text,#F6EBD3);
  background:color-mix(in srgb,var(--color-surface,#1E3A5F) 70%,transparent)}
.rga-btn--danger{--rga-btn-face:#BF574C;--rga-btn-face-2:#963A32;--rga-btn-rim:#561A15;
  --rga-btn-lip:#441410;
  --rga-btn-stroke:0 1px 0 #441410,0 -1px 0 #441410,1px 0 0 #441410,-1px 0 0 #441410}
/* The indigo icon plate (matches skins/btn-icon.png). */
.rga-btn--icon{--rga-btn-face:#2F5694;--rga-btn-face-2:#1D3B6C;--rga-btn-rim:#0E2142;
  --rga-btn-lip:#0A1730}
.rga-btn--icon{padding:0;width:2.75rem;height:2.75rem;border-radius:.8rem}
.rga-btn--pill{border-radius:999px;padding:.35rem .9rem;font-size:.82rem;--rga-btn-depth:3px;
  --rga-btn-face:#28497F;--rga-btn-face-2:#1A335C;--rga-btn-rim:#B48A3A;--rga-btn-lip:#0A1730}
.rga-btn--pill[aria-pressed=true],.rga-btn--pill[aria-selected=true]{--rga-btn-rim:#7A4E10;
  --rga-btn-lip:#5E3B08;--rga-btn-face:#F5D27E;--rga-btn-face-2:#D9A13F;--rga-btn-ink:#14213D;
  --rga-btn-stroke:0 1px 0 rgba(255,240,200,.45)}

/* ── sizes ────────────────────────────────────────────────────────────── */
.rga-btn--md{}
.rga-btn--sm{font-size:.82rem;padding:.4rem .8rem;--rga-btn-depth:3px}
.rga-btn--lg{font-size:1.15rem;padding:.8rem 1.6rem;border-radius:var(--rga-radius-panel,14px);--rga-btn-depth:6px;min-height:3.25rem}
.rga-btn--icon.rga-btn--sm{width:2.75rem;height:2.75rem}
.rga-btn--icon.rga-btn--lg{width:3.25rem;height:3.25rem}

/* ── loading: content kept for width, hidden; a spinner over it ───────── */
.rga-btn.is-loading{cursor:progress}
.rga-btn.is-loading>:not(.rga-btn__spinner){opacity:0}
.rga-btn__spinner{position:absolute;left:50%;top:50%;width:1.1rem;height:1.1rem;margin:-.55rem 0 0 -.55rem;
  border-radius:999px;border:2.5px solid currentColor;border-right-color:transparent;
  animation:rga-btn-spin 700ms linear infinite}
@keyframes rga-btn-spin{to{rotate:360deg}}

/* ── success: a drawn tick (two borders), never a font glyph ─────────── */
.rga-btn.is-success>:not(.rga-btn__tick){opacity:0}
.rga-btn__tick{position:absolute;left:50%;top:50%;width:.55rem;height:1rem;margin:-.62rem 0 0 -.28rem;
  border:solid var(--color-success,#7FD1A8);border-width:0 3.5px 3.5px 0;rotate:45deg;
  filter:drop-shadow(0 1px 0 rgba(4,9,24,.6));animation:rga-btn-tick 320ms cubic-bezier(.34,1.56,.64,1)}
.rga-btn--primary .rga-btn__tick{border-color:#1E5A33;filter:none}
@keyframes rga-btn-tick{0%{scale:.2;opacity:0}100%{scale:1;opacity:1}}

@media (prefers-reduced-motion:reduce){
  .rga-btn,.rga-btn.is-pressed,.rga-btn:active{scale:1 1;transition-duration:.01ms}
  .rga-btn.is-pressed,.rga-btn:active{translate:0 var(--rga-btn-depth)}
  .rga-btn.is-released,.rga-btn__tick{animation:none}
  .rga-btn__spinner{animation-duration:1.6s}
}
}
`;

